import { apiFetch } from "./client";
import type { RoleRef } from "./access";

export type UserStatus = "ACTIVE" | "LOCKED" | "DISABLED";

export type User = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  avatarUrl: string | null;
  status: UserStatus;
  roles: RoleRef[];
  /** True when the user has at least one per-user exception (grant / deny) — sprint 7. */
  hasOverrides: boolean;
  lastLoginAt: string | null;
  createdAt: string;
};

export type Invitation = {
  id: string;
  email: string;
  roles: RoleRef[];
  invitedBy: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
};

export type Page<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
};

const buildPageQuery = (page: number, size: number) =>
  `?page=${page}&size=${size}&sort=createdAt,desc`;

export const adminApi = {
  // Users
  listUsers: (page = 0, size = 20) =>
    apiFetch<Page<User>>(`/admin/users${buildPageQuery(page, size)}`),
  getUser: (id: string) => apiFetch<User>(`/admin/users/${id}`),
  /** Name and phone only — roles and exceptions go through `userAccessApi` (sprint 7 §4.4). */
  updateUser: (id: string, body: { firstName: string; lastName: string; phone?: string | null }) =>
    apiFetch<User>(`/admin/users/${id}`, { method: "PUT", body }),
  enableUser: (id: string) =>
    apiFetch<User>(`/admin/users/${id}/enable`, { method: "POST" }),
  disableUser: (id: string) =>
    apiFetch<User>(`/admin/users/${id}/disable`, { method: "POST" }),
  deleteUser: (id: string) =>
    apiFetch<void>(`/admin/users/${id}`, { method: "DELETE" }),

  // Invitations
  listPendingInvitations: () => apiFetch<Invitation[]>("/admin/invitations"),
  createInvitation: (body: { email: string; roleIds: string[] }) =>
    apiFetch<Invitation>("/admin/invitations", { method: "POST", body }),
  resendInvitation: (id: string) =>
    apiFetch<Invitation>(`/admin/invitations/${id}/resend`, { method: "POST" }),
  revokeInvitation: (id: string) =>
    apiFetch<void>(`/admin/invitations/${id}`, { method: "DELETE" }),
};
